// SiteMap -- North America map with a pin per Active site, colored by
// SHI bucket (same green/amber/red palette as the CHI Dashboard).
// Click a pin for a popup with site + customer info and a link back
// to the customer detail page. Filter row above the map narrows by
// fuel brand, owner, and health bucket.
//
// Uses Leaflet + react-leaflet + OpenStreetMap tiles. No API key,
// no cost. Sites need to be geocoded first via server/scripts/
// geocode-sites.js.
//
// Dave 2026-07-09.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// SHI health -> pin color. Grey = no snapshot yet (new site).
function bucketFor(score) {
  if (score === null || score === undefined) return 'grey';
  if (score >= 80) return 'green';
  if (score >= 50) return 'amber';
  return 'red';
}
const BUCKET_COLORS = {
  green: '#047857',
  amber: '#b45309',
  red:   '#b91c1c',
  grey:  '#6b7280',
};

// Custom SVG pin so we avoid Leaflet's default-icon URL issue and get
// a bucket-colored teardrop for free. cache keyed by color so we build
// each icon once.
const iconCache = {};
function coloredIcon(color) {
  if (iconCache[color]) return iconCache[color];
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
      <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 21 13 21s13-11.25 13-21C26 5.82 20.18 0 13 0z"
            fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
      <circle cx="13" cy="13" r="5" fill="#ffffff"/>
    </svg>`;
  const icon = new L.DivIcon({
    className: 'shi-pin',
    html: svg,
    iconSize:   [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -32],
  });
  iconCache[color] = icon;
  return icon;
}

// Auto-fit the map to the current marker set whenever the filtered
// list changes. Skips when there are no markers so we don't crash
// Leaflet with a null bounds object.
function FitToMarkers({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (!sites || sites.length === 0) return;
    const bounds = L.latLngBounds(sites.map((s) => [s.latitude, s.longitude]));
    if (!bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 10 });
  }, [sites, map]);
  return null;
}

// North America default view (roughly). Used before the sites load
// and if no sites match the filters.
const DEFAULT_CENTER = [50, -95];
const DEFAULT_ZOOM   = 4;

export default function SiteMap() {
  const [sites, setSites]           = useState([]);
  const [missing, setMissing]       = useState(0);
  const [totals, setTotals]         = useState({ active_total: 0, geocoded_total: 0 });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [ownerFilter, setOwnerFilter]     = useState('all');
  const [brandFilter, setBrandFilter]     = useState('all');
  const [bucketFilter, setBucketFilter]   = useState('all');

  useEffect(() => {
    axios.get('/api/map/sites')
      .then((r) => {
        setSites(r.data.sites || []);
        setMissing(r.data.missing_geocoding || 0);
        setTotals(r.data.totals || { active_total: 0, geocoded_total: 0 });
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  const owners = useMemo(() => {
    const set = new Set(sites.map((s) => s.owner_name).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [sites]);
  const brands = useMemo(() => {
    const set = new Set(sites.map((s) => s.fuel_brand).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [sites]);

  const filtered = useMemo(() => {
    return sites.filter((s) => {
      if (ownerFilter !== 'all' && s.owner_name !== ownerFilter) return false;
      if (brandFilter !== 'all' && s.fuel_brand !== brandFilter) return false;
      if (bucketFilter !== 'all' && bucketFor(s.shi_score) !== bucketFilter) return false;
      return true;
    });
  }, [sites, ownerFilter, brandFilter, bucketFilter]);

  const bucketCounts = useMemo(() => {
    const c = { green: 0, amber: 0, red: 0, grey: 0 };
    for (const s of sites) {
      if (ownerFilter !== 'all' && s.owner_name !== ownerFilter) continue;
      if (brandFilter !== 'all' && s.fuel_brand !== brandFilter) continue;
      c[bucketFor(s.shi_score)]++;
    }
    return c;
  }, [sites, ownerFilter, brandFilter]);

  if (error) return <section className="page"><h2>Site Map</h2><p style={{ color: 'var(--color-bad)' }}>Error: {error}</p></section>;

  return (
    <section className="page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--header-height, 56px) - var(--page-pad-y, 24px) * 2)' }}>
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <div>
          <h2 style={{ margin: 0 }}>Site Map <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>Every active site, colored by health</span></h2>
          <div style={{ fontSize: 12, color: 'var(--color-text-dim)', marginTop: 4 }}>
            {loading ? 'Loading...' : `${filtered.length} of ${totals.geocoded_total} geocoded sites shown`}
            {missing > 0 && !loading && (
              <span style={{ marginLeft: 8, color: 'var(--color-warn)' }}>
                ({missing} active site{missing === 1 ? '' : 's'} missing coordinates -- run geocode-sites.js)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filter row + clickable bucket chips */}
      <div style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'center',
        flexWrap: 'wrap', marginBottom: 'var(--space-3)',
      }}>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
          {owners.map((o) => <option key={o} value={o}>{o === 'all' ? 'All owners' : o}</option>)}
        </select>
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
          {brands.map((b) => <option key={b} value={b}>{b === 'all' ? 'All brands' : b}</option>)}
        </select>

        {/* Bucket chips -- same click-to-filter interaction as CHI Dashboard */}
        {[
          { key: 'green', label: 'Green', bg: '#d1fae5', fg: '#047857', count: bucketCounts.green },
          { key: 'amber', label: 'Amber', bg: '#fef3c7', fg: '#b45309', count: bucketCounts.amber },
          { key: 'red',   label: 'Red',   bg: '#fee2e2', fg: '#b91c1c', count: bucketCounts.red   },
        ].map((chip) => {
          const isActive = bucketFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setBucketFilter(isActive ? 'all' : chip.key)}
              style={{
                background: chip.bg, color: chip.fg,
                padding: '4px 10px', borderRadius: 4, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit',
                border: `2px solid ${isActive ? BUCKET_COLORS[chip.key] : 'transparent'}`,
              }}
            >
              {chip.label}: {chip.count}{isActive && ' x'}
            </button>
          );
        })}
        {bucketFilter !== 'all' && (
          <button
            type="button"
            onClick={() => setBucketFilter('all')}
            style={{
              padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)', color: 'var(--color-text-dim)',
              fontFamily: 'inherit', fontSize: 'inherit',
            }}
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Map fills the remaining vertical space */}
      <div style={{
        flex: 1, minHeight: 400, border: '1px solid var(--color-border)',
        borderRadius: 6, overflow: 'hidden',
      }}>
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
        >
          {/* CARTO Voyager basemap — soft colored land/water/roads that
              keep the health-colored pins readable. Free, no API key. */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
            subdomains="abcd"
            maxZoom={20}
          />
          <FitToMarkers sites={filtered} />
          {filtered.map((s) => {
            const bucket = bucketFor(s.shi_score);
            return (
              <Marker
                key={s.site_id}
                position={[s.latitude, s.longitude]}
                icon={coloredIcon(BUCKET_COLORS[bucket])}
              >
                <Popup>
                  <div style={{ minWidth: 220, fontSize: 13 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      {s.site_name || '(unnamed site)'}
                    </div>
                    <div style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
                      #{s.site_number || '--'}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Link to={`/customers/${s.customer_id}`}>{s.company}</Link>
                    </div>
                    <div style={{ marginTop: 6, color: '#374151' }}>
                      {[s.address, s.city, s.province_state].filter(Boolean).join(', ')}
                    </div>
                    <div style={{
                      marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap',
                      fontSize: 12, color: '#374151',
                    }}>
                      {s.fuel_brand && <span><strong>Brand:</strong> {s.fuel_brand}</span>}
                      {s.pos_type && <span><strong>POS:</strong> {s.pos_type}</span>}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px', borderRadius: 4,
                        background: bucket === 'green' ? '#d1fae5' : bucket === 'amber' ? '#fef3c7' : bucket === 'red' ? '#fee2e2' : '#f3f4f6',
                        color:      BUCKET_COLORS[bucket],
                        fontWeight: 700,
                      }}>
                        SHI: {s.shi_score ?? 'n/a'}
                      </span>
                      {s.owner_name && (
                        <span style={{ marginLeft: 8, color: '#6b7280' }}>Owner: {s.owner_name}</span>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </section>
  );
}
